<script>
import { mapState } from "vuex";
import Layout from "@/router/layouts/main";
import { canAccess } from "@/utils/menu-map";
import { GRUPOS_REGULAMENTO, AVISO_ATUALIZACAO } from "@/utils/ranking-regulamento";
import { carregarContextoEstadual, temRankingEstadual } from "@/utils/ranking-estadual";
import { podeVerRanking } from "@/utils/ranking-nacional";

/**
 * Rankings > "Regras e Regulamentos": uma seção por ranking do Ranking Nacional e do Ranking
 * Estadual, com quem participa, o que conta, o desempate e o passo a passo de conferência no
 * i4pro. Tela só de conteúdo (não chama a API de ranking); o texto mora em
 * utils/ranking-regulamento.js.
 *
 * Cada tela de ranking (nacional.vue) tem um botão que abre esta página em `#<id do ranking>`.
 * Secretária vê só as seções dos rankings que ela tem permissão. As regras do Estadual aparecem
 * para todos; o atalho "Ver o ranking" delas, só para quem tem o Ranking Estadual (diretoria).
 */
export default {
  components: { Layout },
  data() {
    return { alvo: "", AVISO_ATUALIZACAO };
  },
  computed: {
    ...mapState({ account: (state) => state.account }),
    // As regras do Estadual aparecem para todos (são públicas); só o atalho "Ver o ranking" do
    // Estadual depende de pertencer a uma diretoria (temEstadual).
    grupos() {
      const user = this.account.user;
      return GRUPOS_REGULAMENTO.map((g) => ({ ...g, secoes: g.secoes.filter((s) => canAccess(user, s.path)) })).filter(
        (g) => g.secoes.length
      );
    },
    temEstadual() {
      return temRankingEstadual();
    },
    // Chave que muda quando o Estadual aparece (contexto carregado depois da 1ª renderização).
    idsVisiveis() {
      return this.grupos.map((g) => g.secoes.map((s) => s.id).join(",")).join("|");
    },
  },
  watch: {
    "$route.hash"() {
      this.irParaAncora();
    },
    idsVisiveis() {
      this.irParaAncora();
    },
  },
  created() {
    carregarContextoEstadual();
  },
  mounted() {
    this.irParaAncora();
  },
  methods: {
    // Consultor lê a regra dos rankings de Regional, mas não ganha o atalho para a tela deles.
    mostraLinkRanking(s) {
      return (!s.estadual || this.temEstadual) && podeVerRanking(this.account.user, s);
    },
    irParaAncora() {
      this.irPara((this.$route.hash || "").replace(/^#/, ""));
    },
    // Pelo índice só rola, sem trocar a URL: navegar pelo router repetiria o guard (que consulta a API).
    irPara(id) {
      if (!id) return;
      this.$nextTick(() => {
        const el = document.getElementById(id);
        if (!el) return;
        this.alvo = id;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        el.focus({ preventScroll: true });
      });
    },
    voltarAoIndice() {
      const el = document.getElementById("rg-indice");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
  },
};
</script>

<template>
  <Layout>
    <div class="rg-page">
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <router-link tag="a" to="/" aria-label="Início">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M5 22h14a2 2 0 0 0 2-2v-9a1 1 0 0 0-.29-.71l-8-8a1 1 0 0 0-1.41 0l-8 8A1 1 0 0 0 3 11v9a2 2 0 0 0 2 2zm5-2v-5h4v5zm-5-8.59 7-7 7 7V20h-3v-5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v5H5z"></path>
            </svg>
          </router-link>
        </li>
        <li class="breadcrumb-item">Rankings</li>
        <li class="breadcrumb-item d-none d-sm-block">Regras e Regulamentos</li>
      </ol>

      <div class="page-title-box mb-3">
        <h3 class="mb-1">Regras e Regulamentos</h3>
        <p class="rg-intro mb-0">
          As regras de cada ranking e o passo a passo para você conferir o seu resultado nos relatórios do i4pro.
          Toque no ranking para ir direto à explicação dele.
        </p>
      </div>

      <div class="rg-bloco rg-bloco-hora rg-bloco-topo" role="note">
        <i class="bx bx-time-five" aria-hidden="true"></i>
        <p class="mb-0"><strong>Atualização dos dados:</strong> {{ AVISO_ATUALIZACAO }}</p>
      </div>

      <!-- Índice -->
      <nav id="rg-indice" class="card rg-indice" aria-label="Rankings deste regulamento">
        <div class="card-body">
          <div v-for="g in grupos" :key="g.key" class="rg-indice-grupo">
            <h2 class="rg-indice-titulo">{{ g.label }}</h2>
            <ul class="rg-indice-lista">
              <li v-for="s in g.secoes" :key="s.id">
                <a :href="'#' + s.id" class="rg-indice-link" @click.prevent="irPara(s.id)">
                  <span>{{ s.titulo }}</span>
                  <i class="bx bx-chevron-right" aria-hidden="true"></i>
                </a>
              </li>
            </ul>
          </div>
          <p v-if="!grupos.length" class="rg-vazio mb-0">Você ainda não tem acesso a nenhum ranking.</p>
        </div>
      </nav>

      <!-- Seções -->
      <template v-for="g in grupos">
        <section
          v-for="s in g.secoes"
          :id="s.id"
          :key="s.id"
          class="card rg-secao"
          :class="{ 'is-alvo': alvo === s.id }"
          tabindex="-1"
          :aria-labelledby="s.id + '-titulo'"
        >
          <div class="card-body">
            <div class="rg-eyebrow">{{ s.menu }} · {{ s.empresa }}</div>
            <div class="rg-hd-row">
              <h2 :id="s.id + '-titulo'" class="rg-titulo">{{ s.titulo }}</h2>
              <div class="rg-badges">
                <span class="rg-badge is-mensal">Mensal</span>
                <span v-if="s.soEstadual" class="rg-badge is-estadual">Só no Estadual</span>
              </div>
            </div>

            <div v-if="s.blocoEstadual" class="rg-bloco">
              <i class="bx bx-map-alt" aria-hidden="true"></i>
              <p class="mb-0"><strong>Como funciona o Ranking Estadual:</strong> {{ s.blocoEstadual }}</p>
            </div>

            <h3 class="rg-sub">Quem participa</h3>
            <ul class="rg-lista">
              <li v-for="(t, i) in s.publico" :key="i">{{ t }}</li>
            </ul>

            <h3 class="rg-sub">O que conta ({{ s.conta.titulo }})</h3>
            <ul class="rg-lista">
              <li v-for="(t, i) in s.conta.itens" :key="i">{{ t }}</li>
            </ul>
            <p v-if="s.conta.exemplo" class="rg-exemplo"><strong>Exemplo:</strong> {{ s.conta.exemplo }}</p>

            <h3 class="rg-sub">Desempate (na ordem)</h3>
            <p v-if="s.notaDesempate" class="rg-nota-curta">{{ s.notaDesempate }}</p>
            <ol class="rg-lista rg-lista-num">
              <li v-for="d in s.desempate" :key="d.nome"><strong>{{ d.nome }}:</strong> {{ d.texto }}</li>
            </ol>

            <h3 class="rg-sub">Como conferir no {{ s.conferencia.sistema }}</h3>
            <div class="rg-bloco rg-bloco-hora">
              <i class="bx bx-time-five" aria-hidden="true"></i>
              <p class="mb-0"><strong>Atualização dos dados:</strong> {{ s.conferencia.atualizacao }}</p>
            </div>
            <ol class="rg-passos">
              <li v-for="(p, i) in s.conferencia.passos" :key="i" class="rg-passo">
                <span class="rg-passo-num" aria-hidden="true">{{ i + 1 }}</span>
                <div class="rg-passo-corpo">
                  <p class="mb-0">{{ p.texto }}</p>
                  <img v-if="p.img" :src="p.img" :alt="p.alt || ''" class="rg-print" loading="lazy" />
                </div>
              </li>
            </ol>
            <p v-if="s.conferencia.fechamento" class="rg-fechamento">
              <i class="bx bx-check-circle" aria-hidden="true"></i> {{ s.conferencia.fechamento }}
            </p>

            <div v-if="s.conferencia.aviso" class="rg-aviso">
              <i class="bx bx-error" aria-hidden="true"></i>
              <p class="mb-0">{{ s.conferencia.aviso }}</p>
            </div>

            <div v-if="s.conferencia.observacao" class="rg-aviso">
              <i class="bx bx-info-circle" aria-hidden="true"></i>
              <div>
                <p class="mb-1"><strong>Observação:</strong> {{ s.conferencia.observacao.texto }}</p>
                <p class="mb-0"><strong>Exemplo:</strong> {{ s.conferencia.observacao.exemplo }}</p>
              </div>
            </div>

            <div v-if="s.extra" class="rg-extra">
              <h4 class="rg-extra-titulo">{{ s.extra.titulo }}</h4>
              <p class="mb-2">{{ s.extra.texto }}</p>
              <img v-if="s.extra.img" :src="s.extra.img" :alt="s.extra.alt || ''" class="rg-print" loading="lazy" />
            </div>

            <div class="rg-rodape">
              <router-link v-if="mostraLinkRanking(s)" :to="s.path" class="rg-link">
                Ver o ranking <i class="bx bx-chevron-right" aria-hidden="true"></i>
              </router-link>
              <button type="button" class="rg-link rg-link-btn" @click="voltarAoIndice">
                <i class="bx bx-up-arrow-alt" aria-hidden="true"></i> Voltar ao índice
              </button>
            </div>
          </div>
        </section>
      </template>
    </div>
  </Layout>
</template>

<style scoped>
.rg-page { max-width: 900px; }
.rg-intro { color: var(--st-text-muted); line-height: 1.6; }

/* Índice */
.rg-indice-grupo + .rg-indice-grupo { margin-top: 16px; }
.rg-indice-titulo {
  font-size: 12px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase;
  color: var(--st-text-muted); margin-bottom: 6px;
}
.rg-indice-lista { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(auto-fit, minmax(min(260px, 100%), 1fr)); gap: 6px; }
.rg-indice-link {
  display: flex; align-items: center; justify-content: space-between; gap: 8px; min-height: 44px;
  padding: 8px 12px; border: 1px solid var(--st-border-soft); border-radius: var(--st-radius);
  background: var(--st-canvas); color: var(--st-text); font-size: 13px; font-weight: 600;
}
.rg-indice-link:hover, .rg-indice-link:focus { border-color: var(--st-brand); color: var(--st-brand); text-decoration: none; }
.rg-indice-link .bx { font-size: 18px; color: var(--st-text-soft); flex-shrink: 0; }
.rg-vazio { font-size: 13px; color: var(--st-text-muted); }

/* Seção */
.rg-secao { scroll-margin-top: 90px; outline: none; transition: box-shadow 0.3s ease; }
.rg-secao.is-alvo { box-shadow: 0 0 0 2px var(--st-brand); }
.rg-eyebrow { font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--st-brand); margin-bottom: 2px; }
.rg-hd-row { display: flex; align-items: center; justify-content: space-between; gap: 8px 12px; flex-wrap: wrap; margin-bottom: 8px; }
.rg-titulo { font-size: 17px; font-weight: 700; color: var(--st-text); margin: 0; }
.rg-badges { display: flex; gap: 6px; flex-wrap: wrap; }
.rg-badge {
  display: inline-block; font-size: 11px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
  padding: 3px 10px; border-radius: 999px; white-space: nowrap;
}
.rg-badge.is-mensal { color: var(--st-brand); background: var(--st-brand-soft); }
.rg-badge.is-estadual { color: var(--st-success-strong); background: var(--st-success-soft); }

.rg-bloco {
  display: flex; gap: 8px; align-items: flex-start; padding: 10px 12px; margin-bottom: 4px;
  border-radius: var(--st-radius); background: var(--st-brand-soft); font-size: 13px; line-height: 1.55; color: var(--st-text);
}
.rg-bloco .bx { font-size: 18px; color: var(--st-brand); flex-shrink: 0; line-height: 1.2; }
.rg-bloco-hora { margin-bottom: 12px; }
.rg-bloco-topo { margin-bottom: 16px; }

.rg-sub {
  font-size: 12px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase;
  color: var(--st-text-muted); margin: 18px 0 6px;
}
.rg-lista { margin: 0; padding-left: 20px; font-size: 14px; line-height: 1.6; color: var(--st-text); }
.rg-lista li + li { margin-top: 4px; }
.rg-exemplo, .rg-nota-curta { font-size: 13px; line-height: 1.55; color: var(--st-text-muted); margin: 8px 0 0; }
.rg-nota-curta { margin: 0 0 6px; }
.rg-exemplo strong { color: var(--st-text); }

/* Passo a passo */
.rg-passos { list-style: none; margin: 0; padding: 0; }
.rg-passo { display: flex; gap: 10px; align-items: flex-start; }
.rg-passo + .rg-passo { margin-top: 10px; }
.rg-passo-num {
  flex: 0 0 auto; width: 26px; height: 26px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; color: var(--st-brand); background: var(--st-brand-soft);
}
.rg-passo-corpo { min-width: 0; flex: 1 1 auto; font-size: 14px; line-height: 1.6; color: var(--st-text); padding-top: 2px; }
.rg-print {
  display: block; width: 100%; max-width: 560px; height: auto; margin-top: 8px;
  border: 1px solid var(--st-border-soft); border-radius: var(--st-radius);
}
.rg-fechamento { display: flex; gap: 6px; align-items: center; font-size: 13px; font-weight: 600; color: var(--st-success-strong); margin: 12px 0 0; }
.rg-fechamento .bx { font-size: 18px; }

.rg-aviso {
  display: flex; gap: 8px; align-items: flex-start; margin-top: 14px; padding: 10px 12px;
  border-radius: var(--st-radius); font-size: 13px; line-height: 1.55;
  background: var(--st-warning-soft); color: var(--st-text);
}
.rg-aviso .bx { font-size: 18px; line-height: 1.2; color: var(--st-warning-strong); flex-shrink: 0; }

.rg-extra {
  margin-top: 14px; padding: 12px 14px; border: 1px solid var(--st-border-soft);
  border-radius: var(--st-radius); background: var(--st-canvas); font-size: 13px; line-height: 1.55; color: var(--st-text);
}
.rg-extra-titulo { font-size: 13px; font-weight: 700; color: var(--st-text); margin-bottom: 4px; }

.rg-rodape {
  display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-wrap: wrap;
  margin-top: 18px; padding-top: 12px; border-top: 1px solid var(--st-border-soft);
}
.rg-link {
  display: inline-flex; align-items: center; gap: 4px; min-height: 40px;
  font-size: 13px; font-weight: 600; color: var(--st-brand);
}
.rg-link-btn { background: none; border: 0; padding: 0; cursor: pointer; color: var(--st-text-muted); }
.rg-link-btn:hover { color: var(--st-brand); }
</style>
